import { DEFAULT_PER_PAGE } from "@/features/admin/components";
import { CURRENCIES, type Package, type PackageCategory, type ProductStatus } from "@nh/shared";
import type { AdminPackagesQuery } from "./packages.api";

/**
 * Cómo se ordena la lista. NO es un parámetro del API.
 *
 * `packageListQuerySchema` declara categoría, estado, página y tamaño; el
 * orden no está en el contrato. Así que el orden se aplica AQUÍ, sobre la
 * página que el servidor ya devolvió, y eso hay que decirlo claro:
 *
 *   con más de una página, ordenar por precio ordena lo que se está
 *   viendo, no el catálogo entero.
 *
 * Se hace así y no mandando un `sort` inventado porque un parámetro que
 * el servidor no declara lo descarta en silencio: el control se movería y
 * no pasaría nada, que es peor que un orden acotado y explicado. En
 * cuanto el contrato declare `sort`, esto se sustituye por un parámetro
 * más en `adminPackagesUrl` y este archivo pierde `sortPackages`.
 */
export const PACKAGE_SORTS = [
  "catalog",
  "discount-desc",
  "price-asc",
  "price-desc",
  "newest",
] as const;

export type PackageSort = (typeof PACKAGE_SORTS)[number];

/**
 * Lo que se ve en la barra de filtros.
 *
 * `""` y no `undefined` para "sin filtro": un `<select>` controlado con
 * `value={undefined}` se vuelve NO controlado a mitad de vida, React
 * avisa por consola y el control deja de responder al botón de limpiar.
 * La traducción de `""` a "no mandes este parámetro" ocurre una sola vez,
 * en `toQuery`.
 */
export interface PackageFilters {
  category: PackageCategory | "";
  status: ProductStatus | "";
  sort: PackageSort;
  page: number;
  perPage: number;
}

export const DEFAULT_FILTERS: PackageFilters = {
  category: "",
  status: "",
  sort: "catalog",
  page: 1,
  // Cinco por defecto, como TODA tabla del sistema (Anahí, 29-ago).
  perPage: DEFAULT_PER_PAGE,
};

export function toQuery(filters: PackageFilters): AdminPackagesQuery {
  return {
    ...(filters.category ? { category: filters.category } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    page: filters.page,
    perPage: filters.perPage,
  };
}

export function hasActiveFilters(filters: PackageFilters): boolean {
  return Boolean(filters.category || filters.status);
}

/**
 * Cambiar cualquier filtro vuelve a la página 1.
 *
 * Sin esto, quien está en la página 3 y filtra por "borrador" —que tiene
 * dos resultados— ve una tabla vacía y concluye que no hay ninguno,
 * cuando lo que pidió fue la tercera página de una lista de una.
 *
 * El ORDEN no cuenta como filtro para esto: no cambia cuántos hay, y
 * saltar a la página 1 al reordenar sacaría a quien está en la 3 de
 * donde estaba mirando.
 */
export function applyFilterChange(
  current: PackageFilters,
  patch: Partial<PackageFilters>,
): PackageFilters {
  const next = { ...current, ...patch };
  const changedFilter = patch.category !== undefined || patch.status !== undefined;
  return changedFilter && patch.page === undefined ? { ...next, page: 1 } : next;
}

/** El precio del paquete en la primera moneda que tenga, para comparar. */
function comparablePrice(pkg: Package): number | undefined {
  for (const currency of CURRENCIES) {
    const cents = pkg.prices[currency];
    if (cents !== undefined) return cents;
  }
  return undefined;
}

/**
 * Ordena la página en mano. NO muta el array que llega.
 *
 * Un paquete sin precio va SIEMPRE al final, ordene como ordene: es un
 * paquete que todavía no se puede cobrar, y colarlo entre los baratos lo
 * escondería justo cuando hay que arreglarlo.
 */
export function sortPackages(items: readonly Package[], sort: PackageSort): Package[] {
  const list = [...items];

  switch (sort) {
    case "catalog":
      return list.sort((a, b) => a.sortOrder - b.sortOrder);
    case "discount-desc":
      return list.sort((a, b) => b.discountPercent - a.discountPercent);
    case "newest":
      return list.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    case "price-asc":
    case "price-desc":
      return list.sort((a, b) => {
        const left = comparablePrice(a);
        const right = comparablePrice(b);
        if (left === undefined && right === undefined) return 0;
        if (left === undefined) return 1;
        if (right === undefined) return -1;
        return sort === "price-asc" ? left - right : right - left;
      });
  }
}
